import React from 'react';
import { 
  Briefcase, 
  Clock, 
  CheckCircle, 
  DollarSign, 
  TrendingUp,
  AlertCircle
} from 'lucide-react';
import type { Matter } from '../../types';

interface MatterStatsProps {
  matters: Matter[];
  isLoading?: boolean;
  className?: string;
}

const MatterStats: React.FC<MatterStatsProps> = ({
  matters,
  isLoading = false,
  className = ''
}) => {
  // Calculate statistics
  const stats = React.useMemo(() => {
    if (isLoading || !matters.length) {
      return {
        total: 0,
        active: 0,
        completed: 0,
        onHold: 0,
        totalBilled: 0,
        averageBilled: 0
      };
    }

    const total = matters.length;
    const active = matters.filter(m => m.status === 'active').length;
    const completed = matters.filter(m => m.status === 'completed').length;
    const onHold = matters.filter(m => m.status === 'on-hold').length;
    const totalBilled = matters.reduce((sum, m) => sum + (m.billedAmount || 0), 0);
    const averageBilled = total > 0 ? totalBilled / total : 0;

    return {
      total,
      active,
      completed,
      onHold,
      totalBilled,
      averageBilled
    };
  }, [matters, isLoading]);

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Stat card component
  const StatCard: React.FC<{
    title: string;
    value: string | number;
    subtitle?: string;
    icon: React.ReactNode;
    color: 'blue' | 'green' | 'yellow' | 'purple' | 'red';
    isLoading?: boolean;
  }> = ({ title, value, subtitle, icon, color, isLoading = false }) => {
    const colorClasses = {
      blue: 'bg-blue-900 text-blue-300 border-blue-700',
      green: 'bg-green-900 text-green-300 border-green-700',
      yellow: 'bg-yellow-900 text-yellow-300 border-yellow-700',
      purple: 'bg-purple-900 text-purple-300 border-purple-700',
      red: 'bg-red-900 text-red-300 border-red-700'
    };

    if (isLoading) {
      return (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 animate-pulse">
          <div className="flex items-center justify-between mb-2">
            <div className="h-4 bg-gray-700 rounded w-20"></div>
            <div className="h-5 w-5 bg-gray-700 rounded"></div>
          </div>
          <div className="h-8 bg-gray-700 rounded w-16 mb-1"></div>
          <div className="h-3 bg-gray-700 rounded w-24"></div>
        </div>
      );
    }

    return (
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 hover:bg-gray-750 transition-colors">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-300">{title}</span>
          <div className={`p-1.5 rounded-md border ${colorClasses[color]}`}>
            {icon}
          </div>
        </div>
        <div className="text-2xl font-bold text-white mb-1">
          {typeof value === 'number' ? value.toLocaleString() : value}
        </div>
        {subtitle && (
          <p className="text-xs text-gray-500">{subtitle}</p>
        )}
      </div>
    );
  };

  return (
    <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 ${className}`}>
      <StatCard
        title="Total Matters"
        value={stats.total}
        subtitle={`${stats.total} matter${stats.total !== 1 ? 's' : ''} total`}
        icon={<Briefcase className="w-4 h-4" />}
        color="blue"
        isLoading={isLoading}
      />

      <StatCard
        title="Active"
        value={stats.active}
        subtitle={`${((stats.active / (stats.total || 1)) * 100).toFixed(0)}% of total`}
        icon={<Clock className="w-4 h-4" />}
        color="green"
        isLoading={isLoading}
      />

      <StatCard
        title="Completed"
        value={stats.completed}
        subtitle={`${((stats.completed / (stats.total || 1)) * 100).toFixed(0)}% completion rate`}
        icon={<CheckCircle className="w-4 h-4" />}
        color="blue"
        isLoading={isLoading}
      />

      <StatCard
        title="On Hold"
        value={stats.onHold}
        subtitle={stats.onHold > 0 ? 'Needs attention' : 'All active'}
        icon={<AlertCircle className="w-4 h-4" />}
        color={stats.onHold > 0 ? 'yellow' : 'green'}
        isLoading={isLoading}
      />

      <StatCard
        title="Total Billed"
        value={formatCurrency(stats.totalBilled)}
        subtitle="Across all matters"
        icon={<DollarSign className="w-4 h-4" />}
        color="purple"
        isLoading={isLoading}
      />

      <StatCard
        title="Avg per Matter"
        value={formatCurrency(stats.averageBilled)}
        subtitle="Average billing"
        icon={<TrendingUp className="w-4 h-4" />}
        color="green"
        isLoading={isLoading}
      />
    </div>
  );
};

export default MatterStats;
